<template>
  <div class="contact-management">
    <div class="page-header">
      <h1>Gestión de Contactos</h1>
      <p>Administra los mensajes de contacto y suscripciones</p>
    </div>

    <!-- Estadísticas -->
    <div class="stats-grid" v-if="stats">
      <div class="stat-card">
        <div class="stat-icon">
          <i class="fa-solid fa-envelope"></i>
        </div>
        <div class="stat-content">
          <h3>{{ stats.contacts?.total_contacts || 0 }}</h3>
          <p>Total Contactos</p>
        </div>
      </div>
      
      <div class="stat-card">
        <div class="stat-icon new">
          <i class="fa-solid fa-bell"></i>
        </div>
        <div class="stat-content">
          <h3>{{ stats.contacts?.new_contacts || 0 }}</h3>
          <p>Nuevos Mensajes</p>
        </div>
      </div>
      
      <div class="stat-card">
        <div class="stat-icon">
          <i class="fa-solid fa-users"></i>
        </div>
        <div class="stat-content">
          <h3>{{ stats.subscriptions?.total_subscriptions || 0 }}</h3>
          <p>Total Suscripciones</p>
        </div>
      </div>
      
      <div class="stat-card">
        <div class="stat-icon active">
          <i class="fa-solid fa-check-circle"></i>
        </div>
        <div class="stat-content">
          <h3>{{ stats.subscriptions?.active_subscriptions || 0 }}</h3>
          <p>Suscripciones Activas</p>
        </div>
      </div>
    </div>

    <!-- Tabs -->
    <div class="tabs">
      <button 
        :class="['tab-btn', { active: activeTab === 'contacts' }]"
        @click="activeTab = 'contacts'">
        <i class="fa-solid fa-envelope"></i>
        Mensajes de Contacto
      </button>
      <button 
        :class="['tab-btn', { active: activeTab === 'subscriptions' }]"
        @click="activeTab = 'subscriptions'">
        <i class="fa-solid fa-users"></i>
        Suscripciones
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-state">
      <i class="fa-solid fa-spinner fa-spin"></i>
      <p>Cargando...</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="error-state">
      <i class="fa-solid fa-exclamation-triangle"></i>
      <p>{{ error }}</p>
      <button @click="loadData" class="retry-btn">Reintentar</button>
    </div>

    <!-- Contactos -->
    <div v-else-if="activeTab === 'contacts' && contacts.length > 0" class="contacts-list">
      <div class="contact-item" v-for="contact in contacts" :key="contact.id">
        <div class="contact-header">
          <div class="contact-info">
            <h4>{{ contact.name }}</h4>
            <p class="contact-email">{{ contact.email }}</p>
            <p class="contact-phone" v-if="contact.phone">{{ contact.phone }}</p>
            <p class="contact-company" v-if="contact.company">{{ contact.company }}</p>
          </div>
          <div class="contact-meta">
            <span :class="['status-badge', contact.status]">{{ getStatusLabel(contact.status) }}</span>
            <span class="contact-date">{{ formatDate(contact.created_at) }}</span>
          </div>
        </div>
        
        <div class="contact-service" v-if="contact.service">
          <strong>Servicio:</strong> {{ contact.service }}
        </div>
        
        <div class="contact-message">
          <strong>Mensaje:</strong>
          <p>{{ contact.message }}</p>
        </div>
        
        <div class="contact-actions">
          <button 
            @click="updateContactStatus(contact.id, 'read')"
            :class="['action-btn', { active: contact.status === 'read' }]"
            v-if="contact.status === 'new'">
            <i class="fa-solid fa-eye"></i>
            Marcar como leído
          </button>
          <button 
            @click="updateContactStatus(contact.id, 'replied')"
            :class="['action-btn', { active: contact.status === 'replied' }]"
            v-if="contact.status !== 'replied'">
            <i class="fa-solid fa-reply"></i>
            Marcar como respondido
          </button>
          <button 
            @click="updateContactStatus(contact.id, 'closed')"
            :class="['action-btn', { active: contact.status === 'closed' }]"
            v-if="contact.status !== 'closed'">
            <i class="fa-solid fa-check"></i>
            Cerrar
          </button>
        </div>
      </div>
    </div>

    <!-- Sin contactos -->
    <div v-else-if="activeTab === 'contacts'" class="empty-state">
      <i class="fa-solid fa-inbox"></i>
      <h3>No hay mensajes de contacto</h3>
      <p>Los mensajes de contacto aparecerán aquí cuando los usuarios envíen formularios.</p>
    </div>

    <!-- Suscripciones -->
    <div v-else-if="activeTab === 'subscriptions' && subscriptions.length > 0" class="subscriptions-list">
      <div class="subscription-item" v-for="subscription in subscriptions" :key="subscription.id">
        <div class="subscription-info">
          <h4>{{ subscription.name || 'Sin nombre' }}</h4>
          <p class="subscription-email">{{ subscription.email }}</p>
          <p class="subscription-source">Fuente: {{ subscription.source }}</p>
        </div>
        <div class="subscription-meta">
          <span :class="['status-badge', subscription.status]">{{ getSubscriptionStatusLabel(subscription.status) }}</span>
          <span class="subscription-date">{{ formatDate(subscription.created_at) }}</span>
        </div>
      </div>
    </div>

    <!-- Sin suscripciones -->
    <div v-else-if="activeTab === 'subscriptions'" class="empty-state">
      <i class="fa-solid fa-users"></i>
      <h3>No hay suscripciones</h3>
      <p>Las suscripciones al newsletter aparecerán aquí.</p>
    </div>
  </div>
</template>

<script>
import apiService from '../config/api.js'

export default {
  name: 'ContactManagementPage',
  data() {
    return {
      activeTab: 'contacts',
      loading: false,
      error: '',
      contacts: [],
      subscriptions: [],
      stats: null
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      this.error = '';
      
      try {
        // Cargar estadísticas
        const statsResponse = await apiService.getContactStats();
        console.log('Stats response:', statsResponse);
        
        if (statsResponse && statsResponse.success) {
          this.stats = statsResponse.data;
        } else {
          console.error('Stats error:', statsResponse?.error);
        }
        
        // Cargar contactos
        const contactsResponse = await apiService.getContacts(50, 0);
        console.log('Contacts response:', contactsResponse);
        
        if (contactsResponse && contactsResponse.success) {
          this.contacts = contactsResponse.data;
        } else {
          console.error('Contacts error:', contactsResponse?.error);
        }
        
        // TODO: Implementar API para suscripciones
        this.subscriptions = [];
        
        // Si no hay datos pero no hay error, mostrar mensaje informativo
        if (!this.stats && !this.contacts.length) {
          this.error = 'No hay datos disponibles. Los formularios de contacto aparecerán aquí cuando los usuarios envíen mensajes.';
        }
        
      } catch (error) {
        console.error('Error loading contact data:', error);
        this.error = 'Error de conexión. Verifica tu conexión a internet e intenta nuevamente.';
      } finally {
        this.loading = false;
      }
    },
    
    async updateContactStatus(id, status) {
      try {
        const response = await apiService.updateContactStatus(id, status);
        if (response.success) {
          // Actualizar el estado local
          const contact = this.contacts.find(c => c.id === id);
          if (contact) {
            contact.status = status;
          }
        }
      } catch (error) {
        console.error('Error updating contact status:', error);
      }
    },
    
    getStatusLabel(status) {
      const labels = {
        'new': 'Nuevo',
        'read': 'Leído',
        'replied': 'Respondido',
        'closed': 'Cerrado'
      };
      return labels[status] || status;
    },
    
    getSubscriptionStatusLabel(status) {
      const labels = {
        'active': 'Activo',
        'inactive': 'Inactivo',
        'unsubscribed': 'Desuscrito'
      };
      return labels[status] || status;
    },
    
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }
}
</script>

<style lang="scss" scoped>
// Variables SCSS definidas localmente

.contact-management {
  padding: 20px;
  
  .page-header {
    margin-bottom: 30px;
    
    h1 {
      font-size: 28px;
      font-weight: 600;
      color: #333;
      margin-bottom: 8px;
    }
    
    p {
      color: #666;
      margin: 0;
    }
  }
  
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 30px;
    
    .stat-card {
      background: white;
      border-radius: 12px;
      padding: 20px;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
      display: flex;
      align-items: center;
      gap: 16px;
      
      .stat-icon {
        width: 50px;
        height: 50px;
        border-radius: 12px;
        background: linear-gradient(135deg, #1f8cc6, #f08908);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 20px;
        
        &.new {
          background: linear-gradient(135deg, #ff6b6b, #ee5a24);
        }
        
        &.active {
          background: linear-gradient(135deg, #2ed573, #7bed9f);
        }
      }
      
      .stat-content {
        h3 {
          font-size: 24px;
          font-weight: 600;
          margin: 0 0 4px 0;
          color: #333;
        }
        
        p {
          margin: 0;
          color: #666;
          font-size: 14px;
        }
      }
    }
  }
  
  .tabs {
    display: flex;
    gap: 10px;
    margin-bottom: 30px;
    border-bottom: 2px solid #e1e5e9;
    
    .tab-btn {
      padding: 12px 24px;
      border: none;
      background: none;
      color: #666;
      font-weight: 500;
      cursor: pointer;
      border-bottom: 2px solid transparent;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      gap: 8px;
      
      &:hover {
        color: #1f8cc6;
      }
      
      &.active {
        color: #1f8cc6;
        border-bottom-color: #1f8cc6;
      }
      
      i {
        font-size: 14px;
      }
    }
  }
  
  .loading-state,
  .error-state,
  .empty-state {
    text-align: center;
    padding: 60px 20px;
    color: #666;
    
    i {
      font-size: 48px;
      margin-bottom: 16px;
      opacity: 0.5;
    }
    
    h3 {
      margin: 16px 0 8px 0;
      color: #333;
    }
    
    p {
      margin: 0;
    }
  }
  
  .error-state {
    color: #dc3545;
    
    .retry-btn {
      margin-top: 16px;
      padding: 8px 16px;
      background: #1f8cc6;
      color: white;
      border: none;
      border-radius: 6px;
      cursor: pointer;
    }
  }
  
  .contacts-list,
  .subscriptions-list {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  
  .contact-item,
  .subscription-item {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    
    .contact-header,
    .subscription-info {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 16px;
      
      @media (max-width: 768px) {
        flex-direction: column;
        gap: 12px;
      }
    }
    
    .contact-info h4,
    .subscription-info h4 {
      margin: 0 0 4px 0;
      color: #333;
      font-size: 18px;
    }
    
    .contact-email,
    .contact-phone,
    .contact-company,
    .subscription-email,
    .subscription-source {
      margin: 2px 0;
      color: #666;
      font-size: 14px;
    }
    
    .contact-meta,
    .subscription-meta {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 8px;
      
      @media (max-width: 768px) {
        align-items: flex-start;
      }
    }
    
    .status-badge {
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 500;
      
      &.new {
        background: #fff3cd;
        color: #856404;
      }
      
      &.read {
        background: #d1ecf1;
        color: #0c5460;
      }
      
      &.replied {
        background: #d4edda;
        color: #155724;
      }
      
      &.closed {
        background: #f8d7da;
        color: #721c24;
      }
      
      &.active {
        background: #d4edda;
        color: #155724;
      }
      
      &.inactive {
        background: #f8d7da;
        color: #721c24;
      }
      
      &.unsubscribed {
        background: #e2e3e5;
        color: #383d41;
      }
    }
    
    .contact-date,
    .subscription-date {
      font-size: 12px;
      color: #666;
    }
    
    .contact-service {
      margin-bottom: 12px;
      padding: 8px 12px;
      background: #f8f9fa;
      border-radius: 6px;
      font-size: 14px;
    }
    
    .contact-message {
      margin-bottom: 16px;
      
      strong {
        display: block;
        margin-bottom: 8px;
        color: #333;
      }
      
      p {
        margin: 0;
        line-height: 1.6;
        color: #666;
      }
    }
    
    .contact-actions {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      
      .action-btn {
        padding: 6px 12px;
        border: 1px solid #e1e5e9;
        background: white;
        border-radius: 6px;
        font-size: 12px;
        cursor: pointer;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        gap: 4px;
        
        &:hover {
          background: #1f8cc6;
          color: white;
          border-color: #1f8cc6;
        }
        
        &.active {
          background: #1f8cc6;
          color: white;
          border-color: #1f8cc6;
        }
        
        i {
          font-size: 10px;
        }
      }
    }
  }
}

// Dark theme
body.dark-theme .contact-management {
  .page-header h1 {
    color: #fff;
  }
  
  .stat-card {
    background: #2c3e50;
    
    .stat-content h3 {
      color: #fff;
    }
  }
  
  .contact-item,
  .subscription-item {
    background: #2c3e50;
    
    .contact-info h4,
    .subscription-info h4 {
      color: #fff;
    }
    
    .contact-service {
      background: #34495e;
    }
    
    .contact-message strong {
      color: #fff;
    }
  }
}
</style> 